import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface CertificateOfOriginDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave: (data: any, changes?: any[]) => void
}

const CertificateOfOriginDialog: React.FC<CertificateOfOriginDialogProps> = ({
  data,
  shipmentId,
  documentId,
  onSave,
}) => {
  // Define sections for Certificate of Origin
  const sections: DocumentSection[] = [
    {
      id: 'documentInfo',
      title: 'Document Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Certificate Number', path: 'certificateNumber' },
        { type: 'field', label: 'Reference Number', path: 'referenceNumber' },
        { type: 'field', label: 'Country of Origin', path: 'countryOfOrigin' },
        { type: 'field', label: 'Origin Criteria', path: 'originCriteria' },
        { type: 'field', label: 'Trade Agreement', path: 'tradeAgreement' },
        { type: 'field', label: 'Is Original', path: 'isOriginal' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Exporter',
          path: 'exporter',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
          ],
        },
        {
          type: 'object',
          label: 'Consignee',
          path: 'consignee',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
          ],
        },
      ],
    },
    {
      id: 'producer',
      title: 'Producer Information',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Producer',
          path: 'producer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Carrier', path: 'shipmentDetails.carrier' },
        { type: 'field', label: 'Vessel', path: 'shipmentDetails.vessel' },
        { type: 'field', label: 'Flight Number', path: 'shipmentDetails.flightNumber' },
        { type: 'field', label: 'Departure Date', path: 'shipmentDetails.departureDate' },
        { type: 'field', label: 'Port of Loading', path: 'shipmentDetails.portOfLoading' },
        { type: 'field', label: 'Port of Discharge', path: 'shipmentDetails.portOfDischarge' },
        { type: 'field', label: 'Destination Country', path: 'shipmentDetails.destinationCountry' },
      ],
    },
    {
      id: 'products',
      title: 'Products Information',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Products',
          path: 'products',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'HS Code', path: 'hsCode' },
            { label: 'Quantity', path: 'quantity' },
            { label: 'Unit of Measure', path: 'unitOfMeasure' },
            {
              label: 'Unit Value',
              path: 'unitValue',
              complexType: 'currency',
            },
            {
              label: 'Total Value',
              path: 'totalValue',
              complexType: 'currency',
            },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
            { label: 'Country of Origin', path: 'countryOfOrigin' },
            { label: 'Manufacturer Name', path: 'manufacturerName' },
            { label: 'Manufacturer Address', path: 'manufacturerAddress' },
          ],
        },
      ],
    },
    {
      id: 'declaration',
      title: 'Declaration',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Declaration Statement', path: 'declaration.statement', fullWidth: true },
        { type: 'field', label: 'Is Sworn Declaration', path: 'declaration.isSworn' },
      ],
    },
    {
      id: 'signatures',
      title: 'Signatures',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Exporter Signature', path: 'exporterSignature' },
        { type: 'field', label: 'Chamber of Commerce Signature', path: 'chamberOfCommerceSignature' },
        { type: 'field', label: 'Government Official Signature', path: 'governmentOfficialSignature' },
        { type: 'field', label: 'Date of Signature', path: 'dateOfSignature' },
        { type: 'field', label: 'Place of Signature', path: 'placeOfSignature' },
        { type: 'field', label: 'Number of Copies', path: 'numberOfCopies' },
      ],
    },
    {
      id: 'certification',
      title: 'Certification',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Issuing Authority', path: 'certification.issuingAuthority' },
        { type: 'field', label: 'Place of Issue', path: 'certification.placeOfIssue' },
        { type: 'field', label: 'Date of Issue', path: 'certification.dateOfIssue' },
        { type: 'field', label: 'Expiry Date', path: 'certification.expiryDate' },
        { type: 'field', label: 'Authorized Signature', path: 'certification.authorizedSignature' },
        { type: 'field', label: 'Stamp or Seal', path: 'certification.stampOrSeal' },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [{ type: 'field', label: 'Additional Information', path: 'additionalInformation', fullWidth: true }],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="Certificate of Origin"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default CertificateOfOriginDialog
